import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Tabs,
  Tab,
  Chip,
  Paper,
  Button,
} from '@mui/material';
import {
  PlayCircleOutline as PlayCircleOutlineIcon,
  ArrowBack as ArrowBackIcon,
  VideoLibrary as VideoLibraryIcon,
} from '@mui/icons-material';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import DonutSmallOutlinedIcon from '@mui/icons-material/DonutSmallOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PersonPinCircleOutlinedIcon from '@mui/icons-material/PersonPinCircleOutlined';
import { Link } from 'react-router-dom';

const videoData = [
  {
    category: 'Geographic Explorer',
    icon: <MapsHomeWorkOutlinedIcon />,
    description: 'Learn how to use location-based data to understand your community demographics.',
    videos: [
      {
        id: 'b6a29b34572143b780c57f6b2620f1d1',
        title: 'Overview of the Geographic Explorer',
        description: "Get started with a complete overview of the tool's capabilities",
        duration: '5:26',
        isOverview: true,
      },
      {
        id: '0d649ff2d72648319e297a1951a6b9e3',
        title: 'GeoX: Your Community Deeper Dive',
        description: "Explore demographic data in your school's community",
        duration: '4:18',
      },
      {
        id: '5bc74c386c4246ddbbc08f63ee6420d6',
        title: 'GeoX: Enrollment Trends Deeper Dive',
        description: 'Analyze enrollment patterns by location over time',
        duration: '6:42',
      },
      {
        id: 'a9eb9189bc5c4b4cad9c7287aed22668',
        title: 'GeoX: Nearby Schools Comparison Deeper Dive',
        description: 'Compare your school to others in the same area',
        duration: '5:10',
      },
    ],
  },
  {
    category: 'Competitive Explorer',
    icon: <DonutSmallOutlinedIcon />,
    description: 'Discover how to analyze and compare your school with nearby alternatives.',
    videos: [
      {
        id: '10c2543490d34a1dbd300543e3ceb54b',
        title: 'Overview of the Competitive Explorer',
        description: 'Learn the fundamentals of competitive analysis tools',
        duration: '4:50',
        isOverview: true,
      },
      {
        id: 'e2dc823b00b24c2d87434dbb9d536848',
        title: 'CompX: Your Community Deeper Dive',
        description: 'Understand your position in the local education market',
        duration: '6:03',
      },
      {
        id: '39ce708acea34c13ad50867780c555ce',
        title: 'CompX: Enrollment & Demographic Trends Deeper Dive',
        description: 'Track changes in enrollment patterns over time',
        duration: '7:12',
      },
      {
        id: 'd72302f699c541cfaf4239a018aafd2d',
        title: 'CompX: Nearby Schools Comparison Deeper Dive',
        description: 'Compare metrics with competing schools in your area',
        duration: '5:36',
      },
    ],
  },
  {
    category: 'Projections Explorer',
    icon: <AutoGraphOutlinedIcon />,
    description: 'Master the tools for forecasting future enrollment scenarios.',
    videos: [
      {
        id: '3e7a737811b14b2cba3f42f6784f8d83',
        title: 'Overview of the Projections Explorer',
        description: 'Learn how to create and interpret enrollment projections',
        duration: '6:18',
        isOverview: true,
      },
      {
        id: '7d90dcaa7aa04a84856bd1bf0a9d103e',
        title: 'ProjectX: Risk Assessment',
        description: 'Identify potential enrollment risks and opportunities',
        duration: '5:42',
      },
      {
        id: 'caebe6dd881b45398daea11abff91414',
        title: 'ProjectX: Strategy Impact Analysis Deeper Dive',
        description: 'Model how different strategies affect future enrollment',
        duration: '8:15',
      },
    ],
  },
  {
    category: 'School Analysis',
    icon: <AssessmentOutlinedIcon />,
    description: 'Discover how to interpret and use comprehensive school data.',
    videos: [
      {
        id: '54e2847091b045539569df7d1fd98df0',
        title: 'Overview of the School Summary',
        description: 'Get a complete tour of the school summary dashboard',
        duration: '4:24',
        isOverview: true,
      },
      {
        id: '7d0fa0c5a5bb47a2aa9c6c56c486def8',
        title: 'Overview of Portfolio Analysis',
        description: 'Learn how to compare metrics across multiple schools',
        duration: '5:56',
        isOverview: true,
      },
    ],
  },
  {
    category: 'Student Catchment Explorer',
    icon: <PersonPinCircleOutlinedIcon />,
    description: 'Learn how to visualize where your students live and identify patterns to inform recruitment and transportation.',
    videos: [
      {
        id: '7f5bbb7e67e7400dbbd1e4079848221c',
        title: 'Getting Started with Student Catchment Explorer',
        description: 'Learn the basics of mapping and analyzing student locations.',
        duration: '4:00',
        isOverview: true,
      },
    ],
  },
];

const tabSx = {
  textTransform: 'uppercase',
  fontWeight: 'medium',
  fontSize: '0.875rem',
  py: 2,
};

const VideoEmbed = ({ videoId, videoTitle }) => (
  <Box
    sx={{
      position: 'relative',
      paddingBottom: '56.25%',
      height: 0,
      overflow: 'hidden',
      maxWidth: '100%',
      mb: 2,
      borderRadius: 1,
    }}
  >
    <iframe
      title={`${videoTitle} - Video Tutorial`}
      src={`https://www.loom.com/embed/${videoId}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`}
      frameBorder="0"
      webkitallowfullscreen="true"
      mozallowfullscreen="true"
      allowFullScreen={true}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
    />
  </Box>
);

const VideoCard = ({ video, onClick }) => {
  const comingSoon = Boolean(video.comingSoon);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            {video.title}
          </Typography>
          {comingSoon ? (
            <Chip label="Coming Soon" size="small" color="secondary" variant="outlined" />
          ) : video.isOverview ? (
            <Chip label="Overview" size="small" color="success" variant="filled" />
          ) : null}
        </Box>
        {video.description && (
          <Typography variant="body2" color="text.secondary">
            {video.description}
          </Typography>
        )}
        {video.duration && !comingSoon && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {video.duration}
          </Typography>
        )}
      </CardContent>
      <Box sx={{ p: 2 }}>
        <Button
          variant={comingSoon ? 'outlined' : 'contained'}
          disabled={comingSoon}
          startIcon={<PlayCircleOutlineIcon />}
          fullWidth
          onClick={() => {
            if (!comingSoon) onClick(video);
          }}
        >
          {comingSoon ? 'Coming Soon' : 'Watch Video'}
        </Button>
      </Box>
    </Card>
  );
};

const CategorySection = ({ category, onVideoClick }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
      <Box sx={{ color: 'primary.main', mt: 0.5 }}>{category.icon}</Box>
      <Box>
        <Typography variant="h6" component="h2" fontWeight="bold">
          {category.category}
        </Typography>
        {category.description && (
          <Typography variant="body2" color="text.secondary">
            {category.description}
          </Typography>
        )}
      </Box>
    </Box>
    <Grid container spacing={3}>
      {category.videos.map((video, index) => (
        <Grid item xs={12} sm={6} md={4} key={video.id || video.title || index}>
          <VideoCard video={video} onClick={onVideoClick} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const VideoTutorials = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedVideo(null);
  };

  const handleVideoClick = (video) => {
    if (video.comingSoon || !video.id) return;
    setSelectedVideo(video);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryForVideo = (videoId) =>
    videoData.find((category) => category.videos.some((video) => video.id === videoId)) || null;

  const selectedCategory = selectedVideo ? getCategoryForVideo(selectedVideo.id) : null;
  const visibleCategories = activeTab === 0 ? videoData : [videoData[activeTab - 1]];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        component={Link}
        to="/support?tab=tutorials"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, color: 'text.secondary', textTransform: 'none' }}
      >
        Back to Support
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        Video Tutorials
      </Typography>
      <Box sx={{ my: 3 }}>
        <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Watch step-by-step guides on using our enrollment planning tools.
        </Typography>
      </Box>

      {selectedVideo && selectedCategory && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Chip
            icon={selectedCategory.icon}
            label={selectedCategory.category}
            size="small"
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
            {selectedVideo.title}
          </Typography>
          {selectedVideo.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedVideo.description}
            </Typography>
          )}
          <VideoEmbed videoId={selectedVideo.id} videoTitle={selectedVideo.title} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setSelectedVideo(null)}>
              Back to All Videos
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ borderRadius: 1, boxShadow: 1, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="standard"
          indicatorColor="primary"
          textColor="primary"
          aria-label="video tutorial categories"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 'auto',
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
            },
            '& .MuiTabs-scroller': {
              overflow: 'visible',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTab-root': {
              ...tabSx,
              flex: { xs: '1 1 50%', md: '1 1 33.33%' },
              maxWidth: { xs: '50%', md: '33.33%' },
              minHeight: 64,
              '&.Mui-selected': {
                borderBottom: '2px solid',
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <Tab
            label="All Tutorials"
            icon={<VideoLibraryIcon />}
            iconPosition="start"
            id="tutorials-tab-0"
          />
          {videoData.map((category, index) => (
            <Tab
              key={category.category}
              label={category.category}
              icon={category.icon}
              iconPosition="start"
              id={`tutorials-tab-${index + 1}`}
            />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {visibleCategories.map((category) => (
            <CategorySection
              key={category.category}
              category={category}
              onVideoClick={handleVideoClick}
            />
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ mt: 4, p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Need Additional Help?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you have specific questions about using these tools, reach out to our support team.
            </Typography>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button
              variant="contained"
              href="mailto:support@educationdatacollective.com"
            >
              Contact Support
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default VideoTutorials;
